/* eslint-disable react/prop-types */
import React, { useState } from 'react';

// ---------- value formatters ----------

const YES_NO = (v) => (v == null || v === '' ? null : Number(v) === 1 ? 'Yes' : 'No');
// A list field renders as one tag per entry; an empty list counts as "not set".
const LIST = (v) => (Array.isArray(v) && v.length ? v : null);

/** "WatchAndHealth" → "Watch and Health" — app enums read as words, not identifiers. */
export const humanize = (v) => String(v ?? '')
  .replace(/([a-z])([A-Z])/g, '$1 $2')
  .replace(/ (And|Or|Of) /g, (m) => m.toLowerCase());
const HUMAN = (v) => (v == null || v === '' ? null : humanize(v));

/**
 * Relative hint for a "DD/MM/YYYY HH:mm:ss" UTC string (the API's date format).
 * Returns null when the value does not parse, so the row shows the date alone.
 */
export const timeAgo = (value, now = Date.now()) => {
  const m = /^(\d{2})\/(\d{2})\/(\d{4}) (\d{2}):(\d{2}):(\d{2})$/.exec(value || '');
  if (!m) return null;
  const then = Date.UTC(+m[3], +m[2] - 1, +m[1], +m[4], +m[5], +m[6]);
  const secs = Math.round((now - then) / 1000);
  if (secs < 60) return 'just now';
  const units = [['year', 31536000], ['month', 2592000], ['day', 86400], ['hour', 3600], ['minute', 60]];
  const [unit, size] = units.find(([, s]) => secs >= s);
  const n = Math.floor(secs / size);
  return `${n} ${unit}${n === 1 ? '' : 's'} ago`;
};

// ---------- field layout ----------

// Small stroke icons, one per section, so the panels can be told apart at a glance.
const ICONS = {
  contact: 'M4 6h16v12H4z M4 7l8 6 8-6',
  health: 'M12 20s-7-4.4-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.6-7 10-7 10z',
  goals: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z M12 16a4 4 0 1 0 0-8 4 4 0 0 0 0 8z M12 12h.01',
  app: 'M7 3h10v18H7z M11 18h2',
  activity: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z M12 7v5l3 2',
  device: 'M8 8h8v8H8z M10 3v3 M14 3v3 M10 18v3 M14 18v3 M3 10h3 M3 14h3 M18 10h3 M18 14h3',
};

// Field: { key, label, format?, date?, copy? }. `date` adds the relative hint; `copy`
// adds a copy-to-clipboard button (ids an admin pastes into another tool). Dates arrive
// from the API already in d/m/Y H:i:s; last_seen is converted from millis to UTC there.
export const FIELD_GROUPS = [
  { title: 'Contact', icon: 'contact', fields: [
    { key: 'id', label: 'User ID', copy: true },
    { key: 'communication_email', label: 'Communication email', copy: true },
    { key: 'mobile_no', label: 'Mobile no' },
    { key: 'country', label: 'Country' },
    { key: 'zip_code', label: 'Zip code' },
  ] },
  { title: 'Health profile', icon: 'health', fields: [
    { key: 'age', label: 'Age' },
    { key: 'weight', label: 'Weight' },
    { key: 'managing_conditions', label: 'Managing condition', format: LIST },
  ] },
  { title: 'Goals & alerts', icon: 'goals', fields: [
    { key: 'pauses_goal', label: 'Pauses goal' },
    { key: 'duration_goal', label: 'Duration goal' },
    { key: 'max_alert', label: 'Max alert' },
  ] },
  { title: 'App settings', icon: 'app', fields: [
    { key: 'dashboard_layout', label: 'Dashboard layout', format: HUMAN },
    { key: 'login_type', label: 'Login type', format: HUMAN },
    { key: 'data_source', label: 'Data source', format: HUMAN },
    { key: 'is_first_launch', label: 'First launch', format: YES_NO },
  ] },
  { title: 'Activity', icon: 'activity', fields: [
    { key: 'registered_time', label: 'Registered (UTC)', date: true },
    { key: 'login_time_utc', label: 'Login time (UTC)', date: true },
    { key: 'last_seen', label: 'Last seen (UTC)', date: true },
    { key: 'last_seen_utc', label: 'Last seen (stored UTC)', date: true },
  ] },
  { title: 'Device & system IDs', icon: 'device', fields: [
    { key: 'watch_app_version', label: 'Watch app version' },
    { key: 'fire_store_user_id', label: 'Firestore user ID', copy: true },
    { key: 'fcm_token', label: 'FCM token', copy: true },
  ] },
];

const fieldValue = (user, { key, format }) => {
  const raw = user[key];
  const v = format ? format(raw) : raw;
  if (Array.isArray(v)) return v;
  return v == null || String(v).trim() === '' ? null : String(v);
};

// ---------- pieces ----------

const SectionIcon = ({ name }) => (
  <span className="ud-fieldgroup__icon" aria-hidden="true">
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor"
      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d={ICONS[name]} />
    </svg>
  </span>
);

const CopyButton = ({ value, label }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard blocked (insecure origin) — the value is still selectable */ }
  };
  return (
    <button type="button" className={`ud-copy${copied ? ' is-done' : ''}`} onClick={copy} aria-label={`Copy ${label}`}>
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
};

const FieldValue = ({ field, value, now }) => {
  if (value == null) return 'Not set';
  if (Array.isArray(value)) {
    return (
      <span className="ud-tags">
        {value.map((item) => <span className="ud-tag" key={item}>{item}</span>)}
      </span>
    );
  }
  const ago = field.date ? timeAgo(value, now) : null;
  return (
    <>
      <span className="ud-fieldlist__text">
        {value}
        {ago && <span className="ud-fieldlist__hint">{ago}</span>}
      </span>
      {field.copy && <CopyButton value={value} label={field.label} />}
    </>
  );
};

/**
 * "Profile details" tab — the rest of the users row, laid out as grouped panels in the
 * wide content column (the narrow identity card keeps only the headline facts). It reads
 * the profile the page already fetched, so opening it costs no request.
 */
const ProfileTab = ({ user, now = Date.now() }) => {
  if (!user) return null;
  return (
    <div className="ud-fields" data-testid="ud-all-fields">
      {FIELD_GROUPS.map((group) => (
        <section className="ud-fieldgroup" key={group.title} data-testid={`ud-fieldgroup-${group.title}`}>
          <header className="ud-fieldgroup__head">
            <SectionIcon name={group.icon} />
            <h3 className="ud-fieldgroup__title">{group.title}</h3>
          </header>
          <dl className="ud-fieldlist">
            {group.fields.map((field) => {
              const v = fieldValue(user, field);
              return (
                <div className="ud-fieldlist__row" key={field.key}>
                  <dt className="ud-fieldlist__k">{field.label}</dt>
                  <dd className={`ud-fieldlist__v${v == null ? ' is-empty' : ''}`} data-testid={`ud-field-${field.key}`}>
                    <FieldValue field={field} value={v} now={now} />
                  </dd>
                </div>
              );
            })}
          </dl>
        </section>
      ))}
    </div>
  );
};

export default ProfileTab;
